import React from 'react';
import Handlebars from 'handlebars';
import {
  IBlockData,
  BasicType,
  AdvancedType,
  createCustomBlock,
  RecursivePartial,
} from '../../..';
import { BasicBlock } from '../../../components/BasicBlock';
import { Section, Column } from '../../../components';
import { merge, get } from 'lodash';
import { getParentByIdx } from '../../../utils/block';
import { normalizeMergeTagKey } from '../../../utils/mergeTagDictionary';
import { proxySignatureImagesForRaster } from '../../../utils/proxySignatureImagesForRaster';
import { fitSignatureTables, pinSignatureImageSizes } from '../../../utils/pinSignatureImageSizes';

// Local copy to avoid importing from the broader utils barrel (which creates a
// circular dependency that breaks block registration). Mirrors the Spacer block.
function isMergeTagExpression(value: string | undefined): boolean {
  return /^\{\{\s*[^{}]+?\s*\}\}$/.test(String(value || '').trim());
}

// A cleared profile signature is not an empty string: the profile WYSIWYG posts
// its innerHTML verbatim, so "cleared" arrives as `<p><br></p>` or `&nbsp;`.
// Treating those as real content rendered an empty white card in previews and
// thumbnails. An image-only signature is legitimate content, hence the <img>
// check before the text test.
function hasSignatureContent(value: unknown): boolean {
  if (typeof value !== 'string' || value === '') return false;
  // Comments go first, before BOTH remaining tests: Word and Outlook
  // downlevel residue can carry a commented-out <img> and text that the tag
  // strip would miss (it stops at the first `>`), and either would read as
  // real content. A live <img> is legitimate content on its own.
  const withoutComments = value.replace(/<!--[\s\S]*?-->/g, '');
  if (/<img\b/i.test(withoutComments)) return true;
  const text = withoutComments
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#x0*a0;|&#8203;|&#x0*200b;/gi, ' ')
    .replace(/\s+/g, '')
    .trim();
  return text.length > 0;
}

function resolveColorTagValue(
  tagExpression: string | undefined,
  dataSource?: Record<string, any>,
): string | null {
  const rawTag = String(tagExpression || '').trim();
  if (!rawTag || !dataSource) return null;
  const extractedKey = rawTag.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() || rawTag;
  const normalizedKey = normalizeMergeTagKey(extractedKey);
  const value = get(dataSource, extractedKey) ?? get(dataSource, normalizedKey);
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return null;
}

interface ISignatureData {
  layout?: 'mobile' | 'desktop'; // For preview mock only
}

interface ISignatureAttributes extends Record<string, any> {
  'background-color': string;
  'padding-top': string;
  'padding-right': string;
  'padding-bottom': string;
  'padding-left': string;
  'color': string;
  'align': string;
  'border-radius': string;
  'border-width': string;
  'border-style': string;
  'border-color': string;
  border: string;
  padding: string;
}

export type ISignature = IBlockData<ISignatureAttributes, ISignatureData>;

const CustomBlockType = {
  SIGNATURE_BLOCK: 'signature_block' as const,
};

export const SignatureBlock = createCustomBlock<ISignature>({
  name: 'Signature',
  type: CustomBlockType.SIGNATURE_BLOCK,
  validParentType: [
    BasicType.PAGE,           // Standalone
    BasicType.WRAPPER,        // Within wrapper
    BasicType.SECTION,        // Layout options
    BasicType.COLUMN,         // Layout options
    BasicType.GROUP,          // Layout options
    BasicType.HERO,           // Layout options
    AdvancedType.WRAPPER,
    AdvancedType.SECTION,
    AdvancedType.COLUMN,
    AdvancedType.GROUP,
    AdvancedType.HERO,
  ],
  create: (payload?: RecursivePartial<ISignature>) => {
    const defaultData: ISignature = {
      type: CustomBlockType.SIGNATURE_BLOCK,
      data: {
        value: {
          layout: 'desktop', // For preview mock only
        },
      },
      attributes: {
        'background-color': 'transparent',
        'padding-top': '0px',
        'padding-right': '0px',
        'padding-bottom': '0px',
        'padding-left': '0px',
        padding: '0px 0px 0px 0px',
        border: '',
        'border-width': '',
        'border-style': '',
        'border-color': '',
        'border-radius': '0px',
        color: '#000000',
        align: 'left',
      },
      children: [],
    };
    return merge(defaultData, payload);
  },
  render: (params: {
    data: ISignature;
    idx?: string | null;
    mode: 'testing' | 'production';
    context?: any;
    dataSource?: { [key: string]: any };
    children?: React.ReactNode;
    keepClassName?: boolean;
    renderPortal?: any;
  }) => {
    const { data, idx, mode } = params;
		const isTest = params.mode === 'testing';

    // Extract attributes for styling
    // In production mode, force transparent background if white is stored
    const storedBgColor = data.attributes['background-color'];
    const backgroundColor = params.mode === 'production' && (!storedBgColor || storedBgColor === '#ffffff')
      ? 'transparent'
      : (storedBgColor || 'transparent');
    const paddingTop = data.attributes['padding-top'] || '0px';
    const paddingRight = data.attributes['padding-right'] || '0px';
    const paddingBottom = data.attributes['padding-bottom'] || '0px';
    const paddingLeft = data.attributes['padding-left'] || '0px';
    const padding = data.attributes.padding || `${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}`;

    // Construct border from individual properties. A dynamic brand colour stored as a
    // merge tag (e.g. "{{user.brandPrimaryColor}}") would be an invalid CSS colour, so
    // resolve it to the real hex when we have a data source (preview/test rendering).
    const borderWidth = data.attributes['border-width'] || '';
    const borderStyle = data.attributes['border-style'] || '';
    const rawBorderColor = data.attributes['border-color'] || '';
    const borderColor =
      isMergeTagExpression(rawBorderColor) && params.dataSource
        ? resolveColorTagValue(rawBorderColor, params.dataSource) || rawBorderColor
        : rawBorderColor;
    const border = data.attributes.border ||
      (borderWidth && borderStyle && borderColor
        ? `${borderWidth} ${borderStyle} ${borderColor}`
        : '');

    const borderRadius = data.attributes['border-radius'] || '0px';

    // Generate CSS classes for editor selectability
    let cssClasses = 'email-block';
    if (isTest && idx) {
      cssClasses = `email-block node-idx-${idx} node-type-${data.type}`;
    }

    // Show skeleton ONLY in editor canvas, NOT in preview modal
    if (isTest && !params.dataSource?.previewMode) {
      // EDITOR CANVAS - Card-like skeleton with left image area and right content
      const skeletonHtml = `
        <div class="${cssClasses}" style="max-width: 600px; min-height: 185px; margin: 0 auto; background: ${backgroundColor}; padding: ${padding}; border: ${border}; border-radius: ${borderRadius}; box-sizing: border-box;">
          <div style="background: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; overflow: hidden; box-sizing: border-box;">

            <div style="display: flex; width: 100%; box-sizing: border-box;">
              <!-- Left image area -->
              <div style="flex: 0 0 30%; background: var(--bs-gray-100); min-height: 180px;">
                <div style="width: 100%; height: 180px; background: var(--bs-gray-100);"></div>
              </div>

              <!-- Right side (content + logo/social) -->
              <div style="flex: 1 1 auto; display: flex; flex-wrap: wrap; background: #ffffff; box-sizing: border-box;">
                <!-- Middle content -->
                <div style="flex: 1 1 auto; padding: 20px 24px; background: #ffffff; box-sizing: border-box;">
                  <div style="height: 18px; width: 45%; background: var(--bs-gray-100); border-radius: 4px; margin-bottom: 10px;"></div>
                  <div style="height: 14px; width: 35%; background: var(--bs-gray-100); border-radius: 4px; margin-bottom: 18px;"></div>
                  <div style="height: 12px; width: 90%; background: var(--bs-gray-100); border-radius: 4px; margin-bottom: 10px;"></div>
                  <div style="height: 12px; width: 80%; background: var(--bs-gray-100); border-radius: 4px; margin-bottom: 10px;"></div>
                  <div style="height: 12px; width: 70%; background: var(--bs-gray-100); border-radius: 4px; margin-bottom: 0;"></div>
                </div>

                <!-- Right content -->
                <div style="flex: 0 0 120px; padding: 20px 16px; text-align: right; background: #ffffff; box-sizing: border-box;">
                  <div style="display: flex; justify-content: flex-end;">
                    <div style="width: 50px; height: 60px; background: var(--bs-gray-100); border-radius: 2px;"></div>
                  </div>
                  <div style="margin-top: 18px; display: flex; justify-content: flex-end; gap: 6px;">
                    <div style="width: 16px; height: 16px; background: var(--bs-gray-100); border-radius: 50%;"></div>
                    <div style="width: 16px; height: 16px; background: var(--bs-gray-100); border-radius: 50%;"></div>
                    <div style="width: 16px; height: 16px; background: var(--bs-gray-100); border-radius: 50%;"></div>
                    <div style="width: 16px; height: 16px; background: var(--bs-gray-100); border-radius: 50%;"></div>
                  </div>
                </div>

								<!-- Disclaimer moved from attribute drawer into block skeleton -->
								<div style="padding: 14px 16px 16px 16px; box-sizing: border-box;">
									<div style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px 18px; font-family: Arial, sans-serif; font-size: 13px; color: var(--bs-gray-500); line-height: 1.45; background: var(--bs-gray-50); max-width: 520px; margin: 0 auto;">
										This block is dynamically populated from the sender's signature in their profile settings and cannot be edited here.
									</div>
								</div>
              </div>

						</div>

          </div>
        </div>
      `;

      const contentNode = (
        <BasicBlock params={params} tag='mj-text'>
          {skeletonHtml}
        </BasicBlock>
      );

      const parentBlockData = params.idx && params.context
        ? getParentByIdx({ content: params.context }, params.idx)
        : null;

      if (parentBlockData) {
        // If dropped at PAGE/WRAPPER, auto-wrap into Section -> Column
        if (
          parentBlockData.type === BasicType.PAGE ||
          parentBlockData.type === BasicType.WRAPPER ||
          parentBlockData.type === AdvancedType.WRAPPER
        ) {
          return (
            <Section padding='0px' text-align='left'>
              <Column>{contentNode}</Column>
            </Section>
          );
        }

        // If dropped at SECTION/GROUP, wrap into a single Column
        if (
          parentBlockData.type === BasicType.SECTION ||
          parentBlockData.type === AdvancedType.SECTION ||
          parentBlockData.type === BasicType.GROUP ||
          parentBlockData.type === AdvancedType.GROUP
        ) {
          return <Column>{contentNode}</Column>;
        }
      }

      // If already in COLUMN/HERO or parent unknown, render content directly
      return contentNode;
    }

    // PRODUCTION/PREVIEW MODE
    let htmlContent: string;

    if (
      params.dataSource?.previewMode &&
      hasSignatureContent(params.dataSource?.AgentEmailSignature)
    ) {
      // Design Manager has no real profile, so its preview passes the mock signature through
      // this branch; it keeps its illustrative card and the mock's markup untouched. The
      // real-signature fixes below apply to ClientBAY only (monday 13065351364).
      const isDesignAdmin = Boolean(params.dataSource?.isDesignAdmin);

      // PREVIEW MODE - the agent's real signature, wrapped exactly like the production send:
      // no extra bordered card, so Preview & Test shows what the recipient gets.
      const templateString = isDesignAdmin
        ? `
        <div class="{{cssClasses}}" style="font-size: initial; max-width: 600px; min-height: 185px; margin: 0 auto; background: {{backgroundColor}}; padding: {{padding}}; border: {{border}}; border-radius: {{borderRadius}};">
          <div style="background: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; overflow: hidden;">
            {{{AgentEmailSignature}}}
          </div>
        </div>
      `
        : `
        <div class="{{cssClasses}}" style="font-size: initial; max-width: 600px; min-height: 185px; margin: 0 auto; background: {{backgroundColor}}; padding: {{padding}}; border: {{border}}; border-radius: {{borderRadius}};">
          {{{AgentEmailSignature}}}
        </div>
      `;

      const templateContext = {
        cssClasses,
        backgroundColor,
        padding,
        border,
        borderRadius,
        // Sizes pinned inline so MJML's head `img { height: auto }` can't square
        // the signature's spacer shims (monday 13065351364).
        AgentEmailSignature: isDesignAdmin
          ? params.dataSource.AgentEmailSignature
          : fitSignatureTables(
              pinSignatureImageSizes(
                params.dataSource.rasterMode
                  ? proxySignatureImagesForRaster(params.dataSource.AgentEmailSignature)
                  : params.dataSource.AgentEmailSignature,
              ),
            ),
      };

      const template = Handlebars.compile(templateString);
      htmlContent = template(templateContext);

    } else if (
      params.dataSource?.previewMode &&
      typeof params.dataSource?.AgentEmailSignature === 'string'
    ) {
      // Real user whose signature is absent or blank — render nothing. The key
      // being present at all is what distinguishes this from Design Manager,
      // which omits it and keeps the illustrative placeholder below.
      return null;

    } else if (params.mode === 'production') {
      // PRODUCTION MODE - Just outer wrapper with transparent background, NO white inner wrapper
      const templateStringProduction = `
        <div class="{{cssClasses}}" style="font-size: initial; max-width: 600px; min-height: 185px; margin: 0 auto; background: {{backgroundColor}}; padding: {{padding}}; border: {{border}}; border-radius: {{borderRadius}};">
          {{{AgentEmailSignature}}}
        </div>
      `;

      const templateContext = {
        cssClasses,
        backgroundColor: data.attributes['background-color'] || 'transparent', // Force transparent if not set
        padding,
        border,
        borderRadius,
        AgentEmailSignature: '{{{AgentEmailSignature}}}',
      };

      const template = Handlebars.compile(templateStringProduction);
      htmlContent = template(templateContext);

    } else {
      // Fallback to generated mock with white wrapper
      const templateString = `
        <div class="{{cssClasses}}" style="font-size: initial; max-width: 600px; min-height: 185px; margin: 0 auto; background: {{backgroundColor}}; padding: {{padding}}; border: {{border}}; border-radius: {{borderRadius}};">
          <div style="background: #ffffff; border-radius: 8px; border: 1px solid #e0e0e0; overflow: hidden;">
            {{{AgentEmailSignature}}}
          </div>
        </div>
      `;

      const templateContext = {
        cssClasses,
        backgroundColor,
        padding,
        border,
        borderRadius,
        AgentEmailSignature: generateMockSignature(),
      };

      const template = Handlebars.compile(templateString);
      htmlContent = template(templateContext);
    }

    // Every branch renders through mj-text, including the real signature. mj-raw
    // is a raw element, so mj-column emits it with no <tr><td> wrapper and the
    // block's Padding attribute stops applying — preview would inset once where
    // the production send (still mj-text) insets twice. The mock already proves
    // arbitrary <table>/<style> signature markup survives mj-text untouched.
    const contentNode = (
      <BasicBlock params={params} tag='mj-text'>
        {htmlContent}
      </BasicBlock>
    );

    // Auto-wrap if needed (like SingleUser block)
    const parentBlockData = params.idx && params.context
      ? getParentByIdx({ content: params.context }, params.idx)
      : null;

    if (parentBlockData) {
      // If dropped at PAGE/WRAPPER, auto-wrap into Section -> Column
      if (
        parentBlockData.type === BasicType.PAGE ||
        parentBlockData.type === BasicType.WRAPPER ||
        parentBlockData.type === AdvancedType.WRAPPER
      ) {
        return (
          <Section padding='0px' text-align='left'>
            <Column>{contentNode}</Column>
          </Section>
        );
      }

      // If dropped at SECTION/GROUP, wrap into a single Column
      if (
        parentBlockData.type === BasicType.SECTION ||
        parentBlockData.type === AdvancedType.SECTION ||
        parentBlockData.type === BasicType.GROUP ||
        parentBlockData.type === AdvancedType.GROUP
      ) {
        return <Column>{contentNode}</Column>;
      }
    }

    // Default: already in COLUMN/HERO or parent unknown
    return contentNode;
  },
});

function generateMockSignature(): string {

  // Mock signature matching the screenshot designs
  return `
			<table cellpadding="0" cellspacing="0" border="0" class="signature-container" style="width: 100%; border-collapse: collapse;">
				<tr>
					<td class="signature-photo" style="width: 160px; vertical-align: top; padding: 20px;">
						<img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces"
									width="140" height="140"
									style="display: block; width: 140px; height: 140px; border-radius: 4px;"
									alt="John Doe" />
					</td>
					<td class="signature-info" style="vertical-align: top; padding: 20px;">
						<div style="font-family: Arial, sans-serif;">
							<div style="font-size: 20px; font-weight: bold; color: #1a1a1a; margin-bottom: 4px;">
								John Doe
							</div>
							<div style="font-size: 13px; color: #666666; margin-bottom: 12px;">
								LN3200584913
							</div>
							<div style="font-size: 14px; color: #333333; line-height: 1.6; margin-bottom: 12px;">
								<div style="margin-bottom: 4px;">
									<span style="color: #888888;">Phone:</span> (555) 903-8506
								</div>
								<div style="margin-bottom: 4px;">
									<a href="mailto:john.doe@email.com" style="color: #0066cc; text-decoration: none;">john.doe@email.com</a>
								</div>
								<div style="margin-bottom: 4px;">
                  <a href="https://backatyou.com" style="color: #0066cc; text-decoration: none;">backatyou.com</a>
								</div>
								<div style="color: #666666; font-size: 13px;">
									700 Main Street Elm, NV 89101
								</div>
							</div>
              <div style="margin-top: 12px;">
                <img src="https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958"
											style="margin-right: 8px; vertical-align: middle; max-width: 100%; height: 32px;"
                    alt="Brokerage Logo" />
							</div>
							<div style="margin-top: 12px;">
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/facebook-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="Facebook" />
								</a>
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/linkedin-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="LinkedIn" />
								</a>
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/twitter-logo-gray.png"
												width="20" height="20"
												style="display: block;"
                        alt="X" />
								</a>
								<a href="#" style="display: inline-block;">
                  <img src="https://bay.cdn.bkat.io/smc/instagram-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="Instagram" />
								</a>
							</div>
						</div>
					</td>
				</tr>
			</table>

    <!-- Mobile responsive styles -->
    <style type="text/css">
      @media only screen and (max-width: 480px) {
        .signature-container tr {
          display: block !important;
        }
        .signature-photo,
        .signature-info {
          display: block !important;
          width: 100% !important;
          text-align: center !important;
          padding: 15px !important;
        }
        .signature-photo img {
          margin: 0 auto !important;
        }
        .signature-info > div {
          text-align: center !important;
        }
        .signature-info a {
          display: inline-block !important;
        }
      }
    </style>
  `;
}
